import {engineContext} from './context.js';

/* ZooInvest Pre-Capital Checkpoint Engine v0.1.0-alpha
 * Engine-only. Final evidence checkpoint before capital.
 * Uses separate BUSINESS / STOCK / TIMING / WHY WRONG dimensions; no universal score.
 * No BUY/SELL. Missing stays missing. Point-in-time inputs only.
 */
(function(root,factory){
 const api=factory();
 if(root) root.ZooInvestPreCapitalCheckpointEngine=api;
})(engineContext,function(){
 'use strict';
 const VERSION='0.1.0-alpha';
 const CONFIG=Object.freeze({calibrated:false,thresholds:'PROVISIONAL / UNCALIBRATED'});
 const norm=v=>String(v??'').trim().toUpperCase();
 const freeze=o=>Object.freeze(o);

 function evidenceState(x){
   if(!x) return 'MISSING';
   const s=norm(x.verification||x.status||x.state);
   if(['VERIFIED','PASS','SUPPORTED','READY'].includes(s)) return 'VERIFIED';
   if(['PARTIAL','REVIEW','PROVISIONAL'].includes(s)) return 'PARTIAL';
   if(['BLOCKED','FAILED','UNVERIFIED'].includes(s)) return s;
   return 'MISSING';
 }

 function dimension(name,input={}){
   const required=Array.isArray(input.required)?input.required:[];
   const rows=required.map(r=>({key:r.key,label:r.label||r.key,state:evidenceState(r.value),critical:r.critical===true}));
   const missing=rows.filter(r=>r.state==='MISSING');
   const blocked=rows.filter(r=>r.state==='BLOCKED'||r.state==='FAILED');
   const unverifiedCritical=rows.filter(r=>r.critical&&r.state==='UNVERIFIED');
   let state='OPEN';
   if(blocked.length||unverifiedCritical.length) state='BLOCKED';
   else if(missing.length||rows.some(r=>['PARTIAL','PROVISIONAL','UNVERIFIED'].includes(r.state))) state='REVIEW';
   else if(rows.length) state='READY';
   return freeze({name,state,rows:freeze(rows.map(freeze)),missing:freeze(missing),blocked:freeze(blocked),unverifiedCritical:freeze(unverifiedCritical)});
 }

 function checkpoint(input={}){
   const business=dimension('BUSINESS',{required:[
     {key:'companyQuality',label:'Company Quality evidence',value:input.companyQuality,critical:true},
     {key:'financialHealth',label:'Financial Health',value:input.financialHealth,critical:true},
     {key:'trajectory',label:'Trajectory',value:input.trajectory,critical:false}
   ]});
   const stock=dimension('STOCK',{required:[
     {key:'stockOpportunity',label:'Stock Opportunity',value:input.stockOpportunity,critical:false},
     {key:'valuation',label:'Valuation / Price Context',value:input.valuation,critical:false},
     {key:'expectations',label:'Expectations',value:input.expectations,critical:false}
   ]});
   const timing=dimension('TIMING',{required:[
     {key:'entryGate',label:'Fresh Entry Gate',value:input.entryGate,critical:true},
     {key:'regime',label:'Market / Regime context',value:input.regime,critical:false},
     {key:'eventRisk',label:'Near-term Event Risk',value:input.eventRisk,critical:false}
   ]});
   const wrong=dimension('WHY_WRONG',{required:[
     {key:'verificationGate',label:'Verification Gate',value:input.verificationGate,critical:true},
     {key:'decisionChallenge',label:'Decision Challenge',value:input.decisionChallenge,critical:false},
     {key:'thesisBreaker',label:'Thesis breaker defined',value:input.thesisBreaker,critical:true}
   ]});

   const dims=freeze({BUSINESS:business,STOCK:stock,TIMING:timing,WHY_WRONG:wrong});
   const arr=Object.values(dims);
   const criticalBlocked=arr.some(d=>d.state==='BLOCKED');
   const reviewNeeded=arr.some(d=>d.state==='REVIEW');
   const state=criticalBlocked?'BLOCKED':reviewNeeded?'REVIEW':arr.every(d=>d.state==='READY')?'READY_FOR_DECISION':'OPEN';

   const blockers=[];
   arr.forEach(d=>{
     d.blocked.forEach(r=>blockers.push(d.name+': '+r.label));
     d.unverifiedCritical.forEach(r=>blockers.push(d.name+': '+r.label+' unverified'));
   });

   return freeze({
     order:freeze(['BUSINESS','STOCK','TIMING','WHY_WRONG']),
     state,
     dimensions:dims,
     blockers:freeze(blockers),
     capitalPermission:false,
     tradeSignal:false,
     buySignal:false,
     sellSignal:false,
     universalInvestmentScore:null,
     companyQualityEffect:0,
     action:state==='BLOCKED'?'INVESTIGATE':state==='REVIEW'?'REVIEW':'READY_FOR_DECISION',
     note:'READY_FOR_DECISION means evidence is sufficiently organized for a human decision; it is not permission or advice to buy.'
   });
 }

 function compareToStory(input={}){
   const claims=Array.isArray(input.claims)?input.claims:[];
   const contradictions=claims.filter(c=>c&&c.claim&&c.evidenceState&&['CONTRADICTED','UNVERIFIED','MISSING'].includes(norm(c.evidenceState)))
     .map(c=>freeze({claim:String(c.claim),evidenceState:norm(c.evidenceState),evidence:c.evidence||null}));
   return freeze({
     status:contradictions.length?'REVIEW':'OPEN',
     contradictions:freeze(contradictions),
     question:contradictions.length?'Which part of the story survives the contradictory or missing evidence?':'No explicit story/evidence contradiction supplied.',
     tradeSignal:false
   });
 }

 function selfTest(){
   const tests=[],t=(n,p)=>tests.push({name:n,pass:!!p});
   const V={verification:'VERIFIED'}, P={verification:'PARTIAL'}, B={state:'BLOCKED'};
   let r=checkpoint({
     companyQuality:V,financialHealth:V,trajectory:V,
     stockOpportunity:V,valuation:V,expectations:V,
     entryGate:V,regime:V,eventRisk:V,
     verificationGate:V,decisionChallenge:V,thesisBreaker:V
   });
   t('Fully verified evidence reaches READY_FOR_DECISION',r.state==='READY_FOR_DECISION');
   t('Ready does not grant capital permission',r.capitalPermission===false);
   t('No universal score exists',r.universalInvestmentScore===null);
   t('No buy or sell signal exists',!r.buySignal&&!r.sellSignal);
   r=checkpoint({companyQuality:V,financialHealth:V,entryGate:B,verificationGate:V,thesisBreaker:V});
   t('Blocked Entry Gate blocks checkpoint',r.state==='BLOCKED');
   r=checkpoint({companyQuality:V,financialHealth:V,entryGate:V,verificationGate:{verification:'UNVERIFIED'},thesisBreaker:V});
   t('Critical unverified Verification Gate blocks checkpoint',r.state==='BLOCKED');
   r=checkpoint({companyQuality:V,financialHealth:V,entryGate:V,verificationGate:V,thesisBreaker:V,valuation:P});
   t('Partial evidence produces REVIEW rather than fake certainty',r.state==='REVIEW');
   t('Missing evidence stays visible',r.dimensions.STOCK.missing.length>0);
   t('Company Quality cannot be rewritten',r.companyQualityEffect===0);
   const c=compareToStory({claims:[{claim:'Profit next year',evidenceState:'UNVERIFIED'},{claim:'Demand strong',evidenceState:'VERIFIED'}]});
   t('Story contradiction layer surfaces unverified claim',c.contradictions.length===1&&c.status==='REVIEW');
   t('Story contradiction does not create trade signal',c.tradeSignal===false);
   t('Canonical order is preserved',JSON.stringify(checkpoint({}).order)===JSON.stringify(['BUSINESS','STOCK','TIMING','WHY_WRONG']));
   return freeze({passed:tests.filter(x=>x.pass).length,total:tests.length,tests});
 }

 const API={VERSION,CONFIG,evidenceState,dimension,checkpoint,compareToStory,selfTest};
 try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
 return freeze(API);
});
